/**
 * ✅ SIMPLIFIED - Calculates unit positions after planned movements
 * This file only exports getLastUnitPosition which tracks where units will be
 * after their planned movement actions.
 */

import useGameStore from '@store/useGameStore';

/**
 * ✅ SIMPLIFIED - Gets the last known position of a unit after all planned movements
 * @param {object} game - The game object (for team_planned_actions)
 * @param {number} unitId - Unit ID
 * @returns {object} Position {x, y} where unit will be after movements, or null
 */
export function getLastUnitPosition(game, unitId) {
  if (!game || !unitId) {
    console.warn('[getLastUnitPosition] Missing game or unitId');
    return null;
  }
  
  // Get unit's base position from store
  const unit = useGameStore.getState().getBaseUnitById(unitId);
  if (!unit) {
    console.warn(`[getLastUnitPosition] Unit ${unitId} not found`);
    return null;
  }
  
  // Start with unit's base position
  let currentPosition = { x: unit.x, y: unit.y };
  
  // Get unit's planned actions
  const unitActions = (game.team_planned_actions || [])
    .filter(a => a.unit_id === unitId)
    .sort((a, b) => a.sequence_number - b.sequence_number);
  
  if (unitActions.length === 0) {
    // No actions, return base position
    return currentPosition;
  }
  
  // Process each action to find movements
  for (const plannedAction of unitActions) {
    // Get the action definition to check if it's movable
    const actionDef = useGameStore.getState().getActionById(plannedAction.action_id);
    
    // Check if this is a movement action
    if (actionDef?.can_move_target && plannedAction.target_data) {
      currentPosition = { ...plannedAction.target_data };
      // console.info(`[getLastUnitPosition] Unit ${unitId} will move to (${currentPosition.x}, ${currentPosition.y})`);
    }
  }
  
  return currentPosition;
}

/**
 * LEGACY - Kept for debug.js compatibility
 * @deprecated Use getLastUnitPosition() instead
 */
export default function calculateActionOrigins(game) {
  console.warn("[calculateActionOrigins] ⚠️ LEGACY FUNCTION - Use getLastUnitPosition() instead");
  
  if (!game) return {};
  
  const units = useGameStore.getState().getBaseUnits();
  const finalOrigins = {};
  
  // Calculate final position for each unit
  for (const unit of units) {
    const lastPosition = getLastUnitPosition(game, unit.id);
    if (lastPosition) {
      finalOrigins[unit.id] = lastPosition;
    }
  }
  
  return finalOrigins;
}